/* eslint-disable eqeqeq */
import type { FormModel } from '../autoSaveModel';
import type { AccessoryPositionModel } from './positionModel';
import {
  distancesToPosition,
  positionToDistances,
  recalcOutputFields,
  ZERO_DISTANCES,
  type MountingDistances,
  type MountingSectionDims,
} from './mountingMath';

/**
 * Phase 6e2b-1 — the client-side MountingModel (§51.4 / parity 333):
 * accessories/mounting/model/mounting.js as a thin stateful wrapper over the
 * pure mountingMath functions. NO URL — fetch() derives, recalc_position()
 * writes the position model (whose own pipeline persists).
 *
 * Legacy listeners carried by the OWNER (useAccessoryForm):
 *  - section c_changed:aw_width/aw_height/length → recalc_output_fields
 *  - accessory change:width_calc/height_calc → recalc_output_fields
 *  - position change → fetch()
 * [NEG]#117: the `c_changed:affecting_view` edge had NO live subscriber
 * (accessory_view passed render() CALL RESULT as handler) — not wired here.
 */

export interface MountingPlaneRow {
  id: number;
  name?: string;
  value?: string;
  [key: string]: unknown;
}

export class MountingModel {
  plane_id: number | null = null;

  plane_value: string | null = null;

  distances: MountingDistances = { ...ZERO_DISTANCES };

  position: AccessoryPositionModel | null = null;

  private accessory: FormModel;

  private getSectionDims: () => MountingSectionDims;

  private getMountingPlanes: () => MountingPlaneRow[];

  private subscribers = new Set<() => void>();

  private version = 0;

  private offPosition: (() => void) | null = null;

  constructor(args: {
    accessory: FormModel;
    position: AccessoryPositionModel | null;
    getSectionDims: () => MountingSectionDims;
    getMountingPlanes: () => MountingPlaneRow[];
  }) {
    this.accessory = args.accessory;
    this.getSectionDims = args.getSectionDims;
    this.getMountingPlanes = args.getMountingPlanes;
    if (args.position) this.setPosition(args.position);
    else this.fetch();
  }

  destroy(): void {
    this.offPosition?.();
    this.subscribers.clear();
  }

  subscribe = (cb: () => void): (() => void) => {
    this.subscribers.add(cb);
    return () => this.subscribers.delete(cb);
  };

  getVersion = (): number => this.version;

  private bump(): void {
    this.version += 1;
    this.subscribers.forEach((cb) => cb());
  }

  toJSON(): Record<string, unknown> {
    return { plane_id: this.plane_id, plane_value: this.plane_value, ...this.distances };
  }

  /**
   * mounting.js footprintWidth/footprintHeight (:172-181) — THE single footprint
   * choke point. Both callers route through it (fetch() :115 and
   * recalc_output_fields() :151), which covers all 12 `width_calc`/`height_calc`
   * reads in mountingMath.ts (pure + correctly parameterised — deliberately NOT
   * touched), and it serves the FORM and the SELECTOR alike (the selector reaches
   * it via accessoryStagingMounting.buildStagingMounting, whose StagingFormAdapter
   * .get is a pass-through onto the staging clone).
   *
   * Combo Louver (KA-5245) — A1.1: a combo louver's footprint in its mounting
   * plane is the ENCLOSURE (width x height), not the shared master louver's
   * width_calc/height_calc. Verbatim legacy:
   *   footprintWidth:  (is_combo && enclosure_width)  ? enclosure_width  : width_calc
   *   footprintHeight: (is_combo && enclosure_height) ? enclosure_height : height_calc
   * The server mirrors this with `recenter_combo_enclosure`
   * (louver_viewset.py:42-83) and `_apply_centering` (:541-544). It matters
   * because for a combo the serializer sets width_calc/height_calc FROM the
   * enclosure anyway (models_accessory.py:486-488) — the override keeps the
   * distances right the moment the user resizes the enclosure, before the
   * round-trip lands new *_calc values.
   */
  private accessorySize() {
    const isCombo = !!this.accessory.get('is_combo');
    const encWidth = this.accessory.get('enclosure_width');
    const encHeight = this.accessory.get('enclosure_height');
    return {
      width_calc:
        Number(isCombo && encWidth ? encWidth : this.accessory.get('width_calc')) || 0,
      height_calc:
        Number(isCombo && encHeight ? encHeight : this.accessory.get('height_calc')) || 0,
    };
  }

  /** mounting.js setPosition (:71-79). */
  setPosition(position: AccessoryPositionModel): void {
    this.offPosition?.();
    this.position = position;
    this.offPosition = position.subscribe(() => this.fetch());
    this.fetch();
  }

  /** mounting.js fetch() override — derive distances from position (:82-136). */
  fetch(): void {
    const planes = this.getMountingPlanes();
    const planeId = this.position ? Number(this.position.get('mounting_plane_id')) : null;
    const plane = this.position ? planes.find((p) => p.id == planeId) : undefined;
    if (plane) {
      const derived = positionToDistances(
        String(plane.value ?? ''),
        Number(this.position!.get('x')) || 0,
        Number(this.position!.get('y')) || 0,
        this.getSectionDims(),
        this.accessorySize()
      );
      this.plane_id = plane.id;
      this.plane_value = String(plane.value ?? '');
      if (derived) this.distances = derived;
    } else {
      this.plane_id = null;
      this.plane_value = null;
    }
    this.recalc_output_fields();
    this.bump();
  }

  /** Plane radio / dist-input writes from the popup (mounting_view §51.4). */
  set(attrs: Partial<MountingDistances> & { plane_id?: number | null; plane_value?: string | null }): void {
    const { plane_id, plane_value, ...dist } = attrs;
    if (plane_id !== undefined) this.plane_id = plane_id;
    if (plane_value !== undefined) this.plane_value = plane_value;
    this.distances = { ...this.distances, ...dist };
    this.recalc_output_fields();
    this.bump();
  }

  /** mounting.js recalc_position (:139-162) — write through to the position. */
  recalc_position(): void {
    if (!this.position) return;
    const pos = distancesToPosition(this.plane_value, this.plane_id, this.distances);
    if (pos) this.position.set(pos);
  }

  /** mounting.js recalc_output_fields (:164-213). */
  recalc_output_fields(): void {
    this.distances = recalcOutputFields(
      this.plane_value,
      this.distances,
      this.getSectionDims(),
      this.accessorySize()
    );
  }
}
